/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AdcModel, ShuttleModel } from '../../../../shared/models';
import { AdcsUtilsService, NamespaceService } from '../../../../shared/services/utils';
import { BaseComponent } from '../../../../../../core/components';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ChartNodeModel, DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { LogService, ModalService, TabService, UtilsService } from '../../../../../../core/services';
import { Observable } from 'rxjs';
import { ShuttleApiService } from '../../../../shared/http';
import { nameValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-edit-shuttle',
    templateUrl: './edit-shuttle.component.html',
    styleUrls: ['./edit-shuttle.component.scss']
})
export class EditShuttleComponent extends BaseComponent implements OnInit {
    /**
     * Stored shuttle id.
     */
    public storedShuttle: ShuttleModel;

    /**
     * Store the active tab ID.
     */
    public activeTabId: string = null;

    /**
     * Form group name.
     */
    public formGroup: FormGroup;

    /**
     * Network dropdown content.
     */
    public networkDropdown: DropdownModel[] = [];

    /**
     * Namespace dropdown content.
     */
    public namespaceDropdown: DropdownModel[] = [];

    /**
     * Namespace selected key.
     */
    public selectedNamespaceKey: number;

    /**
     * Shuttle details for the view.
     */
    public shuttleDetails: any = {
        version: '',
        ip: '',
        legacy: false
    };

    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url.
     */
    public searchTerm: string | [] = '';

    /**
     * Store the search keys.
     */
    public searchKeys = ['id', 'label', 'runningStatus', 'ipAddress', 'network', 'port', 'cidr', 'gateway'];

    /**
     * Store the chart data for chart component.
     */
    public chartData: ChartNodeModel[] = [];

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        gateways: [],
    };

    /**
     * Default selected key for gateway dropdown.
     */
    public selectedKey: any;

    /**
     * Store if subnets can be edited.
     */
    public disableSubnetCrud: boolean = false;

    /**
     * Store if subnets can be deleted.
     */
    public disableDeleteSubnet: boolean = false;

    /**
     * Store the locked state of the form.
     */
    public formLocked: boolean = false;

    /**
     * To store the initial IP form data.
     * @private
     */
    private initialFormData: any = {};

    /**
     * SubnetData
     * @private
     */
    private gatewayData: any[] = [];

    /**
     * Store a newly added gateway name for reference.
     * @private
     */
    private storedAddedGateway: any = null;

    constructor(
        private activatedRoute: ActivatedRoute,
        private adcUtilsService: AdcsUtilsService,
        private cdr: ChangeDetectorRef,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private modalService: ModalService,
        private namespaceService: NamespaceService,
        private router: Router,
        private shuttleApiService: ShuttleApiService,
        private tabService: TabService,
        private utilsService: UtilsService,
    ) {
        super();

        const tabId: string = this.router.getCurrentNavigation()?.extras?.state?.tabFilter;
        if (tabId) {
            this.tabService.activeTabId = tabId;
        }

        // This must be done in the constructor.
        this.storedAddedGateway = this.router.getCurrentNavigation()?.extras?.state;

        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;
        const namespaces = this.namespaceService.namespaces;
        this.setUpForm();
        this.setUpDropdowns(namespaces);
    }

    ngOnInit(): void {
        const id: string = this.activatedRoute.snapshot.paramMap.get('id');
        if (id) {
            this.setUpPageData(parseInt(id, 10));
        } else {
            this.logService.error('EditShuttleComponent:ngOnInit', 'No ID in url');
        }
    }

    /**
     * Set chart data.
     * @param $event
     */
    public setChartData($event: any): void {
        this.selectedKey = $event;
        this.chartData = this.gatewayData.find(item => Object.keys(item)[0] === $event)[$event];
    }

    /**
     * Set the active tab ID.
     * @param $event
     */
    public setActiveTabId($event: any): void {
        this.activeTabId = $event?.id;
    }

    /**
     * Set tag value in form.
     * @param event
     */
    public setTagsFormValue(event: any): void {
        this.adcUtilsService.setTagsFormValue(this.formGroup, event);
    }

    /**
     * Navigate to add page helper.
     */
    public navigateToAddPage(): void {
        this.router.navigateByUrl(
            `/cm/adcs/shuttle/add-subnet/${this.storedShuttle.id}`,
            { state: { selectedGateway: this.selectedKey } })
            .then().catch();
    }

    /**
     * Navigate to edit page helper.
     * @param group
     */
    public navigateToEditPage(group: any): void {
        const id = group.id || group.details.id;
        if (!id) {
            this.logService.error('EditShuttleComponent:navigateToEditPage', 'No id found ' + id);
        } else {
            this.router.navigateByUrl(
                `/cm/adcs/shuttle/edit-subnet/${this.storedShuttle.id}/${id}`,
                { state: { selectedGateway: this.selectedKey } })
                .then().catch();
        }
    }

    /**
     * Update the adc.
     */
    public updateShuttle(): Observable<any> {
        return new Observable<any>(observer => {
            const formValue: any = this.formGroup.getRawValue();
            const id = formValue.id;
            delete formValue?.id;

            if (this.shuttleDetails.legacy && formValue.network) {
                delete formValue.network;
            }

            this.shuttleApiService.update(formValue.namespaceId, id, formValue)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.router.navigate(['/cm/adcs/shuttle'])
                            .then()
                            .catch();

                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Confirm remove adc.
     * @param row
     */
    public confirmDeleteSubnet(row: AdcModel): void {
        const confirmationObject = {
            title: 'subnet_delete',
            message: 'adc_shuttle_edit_subnet_delete_message',
            callBack: this.deleteSubnet.bind(this, row),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete Subnet
     * @param row
     * @private
     */
    private deleteSubnet(row: any): void {
        this.storedAddedGateway = row.gateway;
        const shuttleSubnets = this.formGroup.get('subnets').value;
        const deleteIndex = shuttleSubnets?.findIndex((item: { id: any; }) => item.id === row?.id);
        if (deleteIndex === -1) {
            this.logService.error('EditShuttleComponent:deleteSubnet not found', deleteIndex);
        } else {
            shuttleSubnets?.splice(deleteIndex, 1);
            const formValue: any = this.formGroup.getRawValue();
            const id = formValue.id;
            delete formValue?.id;

            if (this.shuttleDetails.legacy && formValue.network) {
                delete formValue.network;
            }

            this.shuttleApiService.update(formValue.namespaceId, id, formValue)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData(id);
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('EditShuttleComponent:deleteSubnet', 'Error deleting subnet ' + error.message);
                    }
                });
        }
    }

    /**
     * Set up the form for the page.
     * @private
     */
    private setUpForm(): void {
        this.formGroup = this.formBuilder.group({
            id: null,
            locked: [null],
            label: [null, [nameValidation, Validators.required]],
            namespaceId: [null, Validators.required],
            subnets: [[]],
            tags: [null],
            notes: [null, Validators.maxLength(256)]
        });
    }

    /**
     * Set up form subscriber.
     * @private
     */
    private setUpFormSubscriber(): void {
        this.formGroup.valueChanges
            .subscribe({
                next: (): void => {
                    this.utilsService.debounce('formChanges', (): void => {
                        if (!this.utilsService.hasFormDataChanged(this.initialFormData, this.formGroup.getRawValue())) {
                            this.formGroup.markAsPristine();
                        } else {
                            this.formGroup.markAsDirty();
                        }
                    });
                }
            });

        this.formGroup.get('locked').valueChanges
            .subscribe({
                next: (state: boolean): void => {
                    this.formLocked = state;
                    this.chartData = [...this.updateChartDataCallBacks(this.chartData, state)];
                    this.adcUtilsService.lockForm(this.formGroup);
                }
            });
    }

    /**
     * Update the chart data.
     * @param chartData
     * @param state
     * @private
     */
    private updateChartDataCallBacks(chartData: any, state: boolean): any {
        for (const node of chartData) {
            node.details.callBacks = this.getCallBacks(node.type, node, state);
        }

        return chartData;
    }

    /**
     * Set up namespace and network dropdown content.
     * @param namespaces
     * @private
     */
    private setUpDropdowns(namespaces: any[]): void {
        const noAllNamespaceList = namespaces.filter(item => item.key !== 0);

        for (const namespace of noAllNamespaceList) {
            this.namespaceDropdown.push(
                <DropdownModel>{
                    key: namespace.key,
                    value: namespace.value
                }
            );
        }

        for (const item of this.utilsService.createRangeArray(30, 8)) {
            this.networkDropdown.push(
                <DropdownModel>{
                    key: '/' + item.key,
                    value: item.value
                }
            );
        }
    }

    /**
     * Get the shuttle data.
     * @param id
     * @private
     */
    private setUpPageData(id: number): void {
        this.shuttleApiService.get(this.selectedNamespaceKey, id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (shuttle: any): void => {
                    if (shuttle.vendor === 5) {
                        this.disableSubnetCrud = true;
                    }

                    shuttle = this.formatShuttleSubnets(shuttle);
                    this.patchDataToForm(shuttle);
                    this.formatChartData(shuttle.subnets);
                    this.populateTableRows();
                    this.setChartData(this.selectedKey);
                    this.setUpFormSubscriber();
                    this.storedShuttle = shuttle;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'EditShuttleComponent: getShuttle',
                        'Unable to get Shuttle data for ID ' + id, error
                    );
                }
            });
    }

    /**
     * Add Ids to subnets for reference when editing and deleting.
     * @param shuttle
     * @private
     */
    private formatShuttleSubnets(shuttle: ShuttleModel): ShuttleModel {
        if (shuttle.subnets) {
            // Order subnets by gateway, GO doesn't have an order when there isn't an ID.
            const sortedSubnets = this.utilsService.sortArrayByProperty(shuttle.subnets, 'gateway');
            shuttle.subnets = sortedSubnets.map((item: any, index: number) => {
                item['id'] = index;

                return item;
            });
        } else {
            shuttle.subnets = [];
        }

        return shuttle;
    }

    /**
     * Format chart data.
     * @param subnets
     * @private
     */
    private formatChartData(subnets: any): any {
        if (subnets) {
            const data = [];
            const gatewayMap = new Map<string, any>();

            for (const group of subnets) {
                const gateway = group.gateway;

                if (!gatewayMap.has(gateway)) {
                    const parentObject = this.createChartObject(group, 'parent');
                    gatewayMap.set(gateway, [parentObject]);
                }

                const lookup = gatewayMap.get(gateway);
                if (lookup) {
                    lookup.push(this.createChartObject(group, 'child'));
                }
            }

            gatewayMap.forEach((value, key) => {
                data.push({ [key]: value });
            });

            this.gatewayData = data;
            this.setUpGatewayDropdown(data);
        }
    }

    /**
     * Create chart object.
     * @param group
     * @param type
     * @private
     */
    private createChartObject(group: any, type: string): any {
        const itemClass = type === 'child' ? 'subnet' : 'gateway';
        const { height, width } = type === 'child'
            ? { height: 50, width: 150 } : { height: 80, width: 200 };

        const callBacks = this.getCallBacks(type, group, this.formLocked);

        const groupedData = {
            type,
            class: itemClass,
            width,
            height,
            details: {
                id: group.id,
                status: 'online',
                label: group.subnetLabel ? group.subnetLabel : '-',
                subnet: group.subnet,
                gateway: group.gateway,
                callBacks
            }
        };

        if (type === 'parent') {
            delete groupedData.details.label;
            delete groupedData.details.subnet;
        }

        return groupedData;
    }

    /**
     * Get group callbacks.
     * @param type
     * @param group
     * @param lockState
     * @private
     */
    private getCallBacks(type: string, group: any, lockState: boolean): any {
        if (this.disableSubnetCrud) {
            return [];
        } else {
            return type === 'child'
                ? [
                    {
                        label: 'edit',
                        disabled: lockState,
                        callBack: this.navigateToEditPage.bind(this, group)
                    },
                    { label: 'delete', disabled: lockState, callBack: this.confirmDeleteSubnet.bind(this, group) }
                ]
                : [
                    {
                        label: 'subnet_add',
                        disabled: this.formLocked,
                        callBack: this.navigateToAddPage.bind(this)
                    }
                ];
        }
    }

    /**
     * Setup gateway dropdown.
     * @param data
     * @private
     */
    private setUpGatewayDropdown(data: any []): void {
        this.dropdown.gateways = [];
        const excludedGateways = ['127.0.0.1', 'localhost', '::1'];
        for (const item of data) {
            const name: string = Object.keys(item)[0];
            if (!excludedGateways.includes(name)) {
                this.dropdown.gateways.push({ key: name, value: name });
            }
        }

        if (this.storedAddedGateway?.newSubnetGateway || this.selectedKey) {
            const gateway = this.storedAddedGateway?.newSubnetGateway ? this.storedAddedGateway.newSubnetGateway : this.selectedKey;
            const index = this.dropdown.gateways.findIndex(
                (item: { key: any; }) => item.key === gateway
            );

            this.selectedKey = index !== -1 ? this.dropdown.gateways[index].key : this.dropdown.gateways[0].key;
        } else {
            this.selectedKey = this.dropdown.gateways[0].key;
            this.cdr.detectChanges();
        }
    }

    /**
     * Patch API data to form.
     * @param shuttle
     * @private
     */
    private patchDataToForm(shuttle: ShuttleModel): void {
        const defaultNamespace: DropdownModel = this.namespaceDropdown.find(item => item.value === 'default');
        shuttle.namespaceId = shuttle.namespaceID ? shuttle.namespaceID : defaultNamespace.key;
        shuttle.tags = shuttle.tags ? shuttle.tags : [];
        this.shuttleDetails = {
            version: shuttle.version,
            ip: shuttle.ipAddress,
            legacy: shuttle.legacy,
        };

        this.formLocked = shuttle.locked;
        this.formGroup.patchValue(shuttle);

        // TODO review comment added 18/07/24
        if (shuttle.legacy) {
            const timerId = setTimeout((): void => {
                const networkField: AbstractControl = this.formGroup.get('network');
                networkField.disable();
                networkField.updateValueAndValidity();
                networkField.clearValidators();
                clearTimeout(timerId);
            }, 250);
        }

        this.initialFormData = this.utilsService.duplicateObject(this.formGroup.getRawValue());
        this.adcUtilsService.lockForm(this.formGroup);
    }

    /**
     * Populate table rows.
     * @private
     */
    private populateTableRows(): void {
        this.tableRows = [];
        const excludedGateways = ['127.0.0.1', 'localhost', '::1'];
        for (const gatewayGroup of this.gatewayData) {
            const gatewayLabel: string = Object.keys(gatewayGroup)[0];
            const items = gatewayGroup[gatewayLabel];
            for (const item of items) {
                if (item.type !== 'parent' && !excludedGateways.includes(item.details.gateway)) {
                    this.tableRows.push({
                        id: item.details.id,
                        subnetLabel: item.details.label,
                        subnet: item.details.subnet,
                        gateway: item.details.gateway,
                        gatewayLabel: item.details.gatewayLabel || ''
                    });
                }
            }
        }

        this.disableDeleteSubnet = this.tableRows.length <= 1;
    }
}
